type PricingPlan = {
  name: string;
  slug: string;
  monthly: string;
  yearlyTotal?: string;
  value: string;
};

type BillingPeriod = "monthly" | "yearly";

// schema.org prices are plain numbers: "1,430.99" in the pricing feed → "1430.99".
export const schemaPrice = (price: string) => price.replace(/,/g, "");

// UnitPriceSpecification tells crawlers and AI answers the price is per month
// or per year, not a one-time charge (MON / ANN are UN/CEFACT unit codes).
const planPriceSpecification = (price: string, period: BillingPeriod) => {
  const unitCode = period === "monthly" ? "MON" : "ANN";
  return {
    "@type": "UnitPriceSpecification",
    price: schemaPrice(price),
    priceCurrency: "USD",
    unitCode,
    billingDuration: period === "monthly" ? "P1M" : "P1Y",
    referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode },
  };
};

export const buildPrimarySoftwareOffer = (
  plans: readonly PricingPlan[],
  pricingUrl: string,
) => {
  const entryPlan = [...plans].sort(
    (left, right) => Number(left.monthly) - Number(right.monthly),
  )[0];

  if (!entryPlan) {
    throw new Error("Workspace369 pricing requires at least one plan.");
  }

  return {
    "@type": "Offer",
    name: `${entryPlan.name} plan`,
    price: schemaPrice(entryPlan.monthly),
    priceCurrency: "USD",
    priceSpecification: planPriceSpecification(entryPlan.monthly, "monthly"),
    url: `${pricingUrl}#${entryPlan.slug}`,
    availability: "https://schema.org/InStock",
    category: "Subscription",
    description: entryPlan.value,
  };
};

// One Offer per plan and billing period, prices straight from the pricing feed.
export const buildPlanOffers = (
  plans: readonly PricingPlan[],
  pricingUrl: string,
) =>
  plans.flatMap((plan) => {
    const periods: [BillingPeriod, string | undefined][] = [
      ["monthly", plan.monthly],
      ["yearly", plan.yearlyTotal],
    ];
    return periods
      .filter((entry): entry is [BillingPeriod, string] => Boolean(entry[1]))
      .map(([period, price]) => ({
        "@type": "Offer",
        name: `${plan.name}, billed ${period}`,
        price: schemaPrice(price),
        priceCurrency: "USD",
        priceSpecification: planPriceSpecification(price, period),
        url: `${pricingUrl}#${plan.slug}`,
        availability: "https://schema.org/InStock",
        category: "Subscription",
        description: plan.value,
        itemOffered: { "@id": "https://workspace369.com/#software" },
      }));
  });

// A free /tools page is a browser app of its own: name, URL and a $0 offer tell search and
// AI answers it is free to use without signup, apart from the paid Workspace369 product.
export const buildFreeToolApplication = ({
  name,
  url,
  description,
}: {
  name: string;
  url: string;
  description?: string;
}) => ({
  "@type": "WebApplication",
  "@id": `${url}#tool`,
  name,
  url,
  ...(description ? { description } : {}),
  applicationCategory: "BusinessApplication",
  operatingSystem: "Any (web browser)",
  browserRequirements: "Requires JavaScript",
  isAccessibleForFree: true,
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  publisher: { "@id": "https://workspace369.com/#organization" },
  isPartOf: { "@id": "https://workspace369.com/#website" },
});
